import React from 'react';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import { isDependencyConflict } from './deleteConflict';

/** The record the dialog is asking about. */
export type DeleteTarget = {
    id: string;
    /** Shown in the prompt so the user can see what they are about to remove. */
    name: string;
};

type UseDeleteConfirmParams = {
    /** Fires the delete. Resolves to the envelope so a 409 can be read off it. */
    onDelete: (id: string) => Promise<ApiResponse<unknown>>;
    /**
     * Fires the archive, when the entity has one. Omitted for entities where
     * archiving is not the way out (a category, a brand — those are reassigned),
     * and the dialog then offers no secondary action.
     */
    onArchive?: (id: string) => Promise<ApiResponse<unknown>>;
};

/**
 * Drives a delete that the server may refuse (BOS-121).
 *
 * The refusal is the point. A delete used to be fire-and-forget — the row went
 * and nothing could say otherwise — so the UI had nowhere to put an answer.
 * Now the server replies with a sentence naming what depends on the record and
 * how to proceed, and that sentence has to land somewhere the user can read it:
 * inline in the dialog they are already looking at, not in a toast that leaves
 * before they finish the first clause.
 */
export const useDeleteConfirm = ({
    onDelete,
    onArchive,
}: UseDeleteConfirmParams) => {
    const [target, setTarget] = React.useState<DeleteTarget | null>(null);
    const [error, setError] = React.useState<ErrorResponse | null>(null);
    const [isPending, setIsPending] = React.useState(false);

    const onRequestDelete = React.useCallback((next: DeleteTarget) => {
        setTarget(next);
        setError(null);
    }, []);

    const onCancel = React.useCallback(() => {
        setTarget(null);
        setError(null);
        setIsPending(false);
    }, []);

    const onConfirm = React.useCallback(async () => {
        if (target === null) {
            return;
        }
        setIsPending(true);
        setError(null);
        try {
            const result = await onDelete(target.id);
            if (result.success) {
                setTarget(null);
                return;
            }
            // Kept open on failure, whatever the reason: a refusal the user
            // cannot read is the same as no refusal at all.
            setError(result.error);
        } finally {
            setIsPending(false);
        }
    }, [onDelete, target]);

    const onArchiveInstead = React.useCallback(async () => {
        if (target === null || onArchive === undefined) {
            return;
        }
        setIsPending(true);
        setError(null);
        try {
            const result = await onArchive(target.id);
            if (result.success) {
                setTarget(null);
                return;
            }
            setError(result.error);
        } finally {
            setIsPending(false);
        }
    }, [onArchive, target]);

    return {
        target,
        error,
        isPending,
        /**
         * Offer the archive only once the delete has actually been refused for
         * dependants. Before that it is a second destination on a dialog that
         * asked one question, and after any other failure it is not the remedy.
         */
        canArchive: onArchive !== undefined && isDependencyConflict(error),
        onArchiveInstead,
        onCancel,
        onConfirm,
        onRequestDelete,
    };
};
